import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../auth/AuthContext';
import { useCompany } from '../Company/CompanyContext';
import ChatWindow from './ChatWindow';
import WelcomeChat from './WelcomeChat';
import FloatingSidebar from './FloatingSidebar';
import FileUpload from './FileUpload';
import { useParams, useNavigate } from 'react-router-dom';
import LordIcon from '../common/LordIcon';
import scorecardicon from '../../assets/icons/assignment.json';
import rocksicon from '../../assets/icons/heart.json';
import vtoicon from '../../assets/icons/view.json';
import acccharticon from '../../assets/icons/article.json';
import coreicon from '../../assets/icons/book.json';
import trash from '../../assets/icons/trash.json';
import fileIcon from '../../assets/icons/file.json';

const ChatContainer = ({ onToggleExpand, isExpanded }) => {
  const [expandAnimation, setExpandAnimation] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hideTimeout, setHideTimeout] = useState(null);
  const [activeView, setActiveView] = useState('chat');
  const [documentData, setDocumentData] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { chatId } = useParams();
  const { currentUser } = useAuth();
  const { companyInfo } = useCompany();
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleViewChange = (event) => {
      const { view } = event.detail;
      setActiveView(view);
    };
    
    window.addEventListener('chat-view-change', handleViewChange);
    
    return () => {
      window.removeEventListener('chat-view-change', handleViewChange);
    };
  }, []);

  // Set up state and refs
  useEffect(() => {
    // ... existing code ...
    
    // Listen for view change events (for new chat creation)
    const handleViewChange = (event) => {
      if (event.detail && event.detail.view === 'chat') {
        setActiveView('chat');
      }
    };
    
    window.addEventListener('chat-view-change', handleViewChange);
    
    return () => {
      // ... existing cleanup code ...
      window.removeEventListener('chat-view-change', handleViewChange);
    };
  }, [chatId, navigate]);

  // Fetch documents from Firestore when the component mounts or chatId/activeView changes
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!currentUser) return;
      
      try {
        setIsLoading(true);
        
        // Determine which documents to fetch based on company and chat
        const documentsRef = collection(db, 'documents');
        let documentsQuery;
        
        if (companyInfo?.id) {
          // If we have a company ID, get documents for this company
          documentsQuery = query(
            documentsRef, 
            where('companyId', '==', companyInfo.id)
          );
        } else {
          // Otherwise, just get documents for this user
          documentsQuery = query(
            documentsRef, 
            where('userId', '==', currentUser.uid)
          );
        }
        
        const querySnapshot = await getDocs(documentsQuery);
        
        if (!querySnapshot.empty) {
          // Process and organize documents by category
          const docs = {};
          
          querySnapshot.forEach((docSnapshot) => {
            const data = docSnapshot.data();
            const category = data.category || 'general';
            
            if (!docs[category]) {
              docs[category] = [];
            }
            
            docs[category].push({
              id: docSnapshot.id,
              name: data.name,
              type: data.type,
              content: data.content,
              uploadDate: data.createdAt ? data.createdAt.toDate().toISOString() : new Date().toISOString(),
              category: category
            });
          });
          
          setDocumentData(docs);
          console.log(`Loaded ${querySnapshot.size} documents from Firestore`);
        } else {
          console.log('No documents found in Firestore');
          setDocumentData({});
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDocuments();
  }, [currentUser, companyInfo, chatId, activeView]);

  const handleExpandClick = () => {
    setExpandAnimation(true);
    setTimeout(() => {
      onToggleExpand();
      setTimeout(() => {
        setExpandAnimation(false);
      }, 50);
    }, 300);
  };

  const handleMouseEnter = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    // Add a small delay before hiding to prevent flickering
    const timeout = setTimeout(() => {
      setIsHovering(false);
    }, 300);
    setHideTimeout(timeout);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [hideTimeout]);
  
  // Handle file upload for document context
  const handleDocumentUpload = async (processedFiles) => {
    try {
      // Store the processed document content for current context type
      const documentContent = processedFiles.map(file => ({
        name: file.name,
        type: file.type,
        content: file.text,
        uploadDate: new Date().toISOString()
      }));
      
      // Update local document data for current view type
      setDocumentData(prev => ({
        ...prev,
        [activeView]: [...(prev[activeView] || []), ...documentContent]
      }));
      
      // Also save to Firestore for persistence
      const documentsCollection = collection(db, 'documents');
      
      // Get user and company info for document storage
      const userId = currentUser.uid;
      const companyId = companyInfo?.id;
      
      // Save each document to Firestore
      for (const doc of documentContent) {
        try {
          await addDoc(documentsCollection, {
            name: doc.name,
            type: doc.type,
            size: doc.size || 0,
            category: activeView,
            content: doc.content,
            createdAt: new Date(),
            userId: userId,
            companyId: companyId || null,
            chatId: chatId || null
          });
          
          console.log(`Document "${doc.name}" saved to Firestore in category "${activeView}"`);
        } catch (error) {
          console.error('Error saving document to Firestore:', error);
        }
      }
    } catch (error) {
      console.error('Error handling document upload:', error);
    }
  };
  
  // Handle document deletion
  const handleDeleteDocument = async (index) => {
    try {
      const docsInCategory = documentData[activeView] || [];
      if (index >= 0 && index < docsInCategory.length) {
        const docToDelete = docsInCategory[index];
        
        // Remove from local state
        const newDocs = [...docsInCategory];
        newDocs.splice(index, 1);
        
        setDocumentData(prev => ({
          ...prev,
          [activeView]: newDocs
        }));
        
        // Delete from Firestore if it has an ID
        if (docToDelete.id) {
          try {
            await deleteDoc(doc(db, 'documents', docToDelete.id));
            console.log(`Document deleted from Firestore: ${docToDelete.id}`);
          } catch (error) {
            console.error('Error deleting document from Firestore:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  // Handle document updates from the ChatWindow component
  const handleDocumentsUpdated = (updatedDocuments) => {
    console.log('Documents updated from ChatWindow:', updatedDocuments);
    setDocumentData(updatedDocuments);
  };

  // Define animation variants for view transitions
  const viewVariants = {
    initial: { 
      opacity: 0,
      y: 20,
      scale: 0.98,
    },
    animate: { 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1.0],
      }
    },
    exit: { 
      opacity: 0,
      y: -20,
      scale: 0.98,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      }
    }
  };

  // Return the appropriate view component with animation
  const getViewComponent = (view) => {
    switch (view) {
      case 'scorecard':
        return (
          <motion.div 
            key="scorecard-view"
            className="h-full flex flex-col p-8"
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <div className="bg-white/50 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-orange-100/50 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                <LordIcon 
                    icon={scorecardicon} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                  <h2 className="text-2xl font-semibold text-orange-700">Scorecard Context</h2>
                </div>
              </div>
              
              <p className="text-orange-600 mb-4">
                Upload your EOS Scorecard documents to include them as context in your chats. These documents will help the AI understand your measurables and provide more accurate answers.
              </p>
              
              <div className="bg-orange-50/50 rounded-lg p-4 mb-4 border border-orange-100">
                <h3 className="font-medium text-orange-700 mb-2">Current Documents</h3>
                {documentData.scorecard && documentData.scorecard.length > 0 ? (
                  <ul className="max-h-48 overflow-y-auto bg-white/70 rounded-lg divide-y divide-orange-100">
                    {documentData.scorecard.map((doc, index) => (
                      <li key={index} className="flex items-center justify-between p-3">
                        <div className="flex items-center">
                          <LordIcon 
                            icon={fileIcon} 
                            size={20} 
                            trigger="hover"
                            colors="primary:#f97316"
                          />
                          <div>
                            <p className="text-sm font-medium">{doc.name}</p>
                            <p className="text-xs text-gray-500">Uploaded {new Date(doc.uploadDate).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleDeleteDocument(index)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1"
                        >
                          <LordIcon 
                            icon={trash} 
                            size={20} 
                            trigger="hover"
                            colors="primary:#ef4444"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">No Scorecard documents uploaded yet.</p>
                )}
              </div>
              
              <FileUpload 
                onFileUpload={handleDocumentUpload} 
                disabled={isUploading}
                onExtractingStateChange={setIsUploading}
              />
            </div>
          </motion.div>
        );
      case 'vto':
        return (
          <motion.div 
            key="vto-view"
            className="h-full flex flex-col p-8"
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <div className="bg-white/50 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-orange-100/50 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                <LordIcon 
                    icon={vtoicon} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                  <h2 className="text-2xl font-semibold text-orange-700">VTO Context</h2>
                </div>
              </div>
              
              <p className="text-orange-600 mb-4">
                Upload your Vision/Traction Organizer (VTO) documents to include them as context in your chats. These documents will help the AI understand your company's vision and goals.
              </p>
              
              <div className="bg-orange-50/50 rounded-lg p-4 mb-4 border border-orange-100">
                <h3 className="font-medium text-orange-700 mb-2">Current Documents</h3>
                {documentData.vto && documentData.vto.length > 0 ? (
                  <ul className="max-h-48 overflow-y-auto bg-white/70 rounded-lg divide-y divide-orange-100">
                    {documentData.vto.map((doc, index) => (
                      <li key={index} className="flex items-center justify-between p-3">
                        <div className="flex items-center">
                          <LordIcon 
                            icon={fileIcon} 
                            size={20} 
                            trigger="hover"
                            colors="primary:#f97316"
                          />
                          <div>
                            <p className="text-sm font-medium">{doc.name}</p>
                            <p className="text-xs text-gray-500">Uploaded {new Date(doc.uploadDate).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleDeleteDocument(index)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1"
                        >
                          <LordIcon 
                            icon={trash} 
                            size={20} 
                            trigger="hover"
                            colors="primary:#ef4444"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">No VTO documents uploaded yet.</p>
                )}
              </div>
              
              <FileUpload 
                onFileUpload={handleDocumentUpload} 
                disabled={isUploading}
                onExtractingStateChange={setIsUploading}
              />
            </div>
          </motion.div>
        );
      case 'rocks':
        return (
          <motion.div 
            key="rocks-view"
            className="h-full flex flex-col p-8"
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <div className="bg-white/50 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-orange-100/50 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                <LordIcon 
                    icon={rocksicon} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                  <h2 className="text-2xl font-semibold text-orange-700">Rocks Context</h2>
                </div>
              </div>
              
              <p className="text-orange-600 mb-4">
                Upload your Rocks documents to include them as context in your chats. These documents will help the AI understand your quarterly priorities and provide more relevant assistance.
              </p>
              
              <div className="bg-orange-50/50 rounded-lg p-4 mb-4 border border-orange-100">
                <h3 className="font-medium text-orange-700 mb-2">Current Documents</h3>
                {documentData.rocks && documentData.rocks.length > 0 ? (
                  <ul className="max-h-48 overflow-y-auto bg-white/70 rounded-lg divide-y divide-orange-100">
                    {documentData.rocks.map((doc, index) => (
                      <li key={index} className="flex items-center justify-between p-3">
                        <div className="flex items-center">
                          <LordIcon 
                            icon={fileIcon} 
                            size={20} 
                            trigger="hover"
                            colors="primary:#f97316"
                          />
                          <div>
                            <p className="text-sm font-medium">{doc.name}</p>
                            <p className="text-xs text-gray-500">Uploaded {new Date(doc.uploadDate).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleDeleteDocument(index)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1"
                        >
                          <LordIcon 
                            icon={trash} 
                            size={20} 
                            trigger="hover"
                            colors="primary:#ef4444"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">No Rocks documents uploaded yet.</p>
                )}
              </div>
              
              <FileUpload 
                onFileUpload={handleDocumentUpload} 
                disabled={isUploading}
                onExtractingStateChange={setIsUploading}
              />
            </div>
          </motion.div>
        );
      case 'accountability_chart':
        return (
          <motion.div 
            key="accountability-chart-view"
            className="h-full flex flex-col p-8"
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <div className="bg-white/50 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-orange-100/50 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-orange-500 mr-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <h2 className="text-2xl font-semibold text-orange-700">Accountability Chart</h2>
                </div>
              </div>
              
              <p className="text-orange-600 mb-4">
                Upload your Accountability Chart documents to include them as context in your chats. These documents will help the AI understand your organizational structure, roles, and responsibilities.
              </p>
              
              <div className="bg-orange-50/50 rounded-lg p-4 mb-4 border border-orange-100">
                <h3 className="font-medium text-orange-700 mb-2">Current Documents</h3>
                {documentData.accountability_chart && documentData.accountability_chart.length > 0 ? (
                  <ul className="max-h-48 overflow-y-auto bg-white/70 rounded-lg divide-y divide-orange-100">
                    {documentData.accountability_chart.map((doc, index) => (
                      <li key={index} className="flex items-center justify-between p-3">
                        <div className="flex items-center">
                          <LordIcon 
                            icon={fileIcon} 
                            size={20} 
                            trigger="hover"
                            colors="primary:#f97316"
                          />
                          <div>
                            <p className="text-sm font-medium">{doc.name}</p>
                            <p className="text-xs text-gray-500">Uploaded {new Date(doc.uploadDate).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleDeleteDocument(index)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1"
                        >
                          <LordIcon 
                            icon={trash} 
                            size={20} 
                            trigger="hover"
                            colors="primary:#ef4444"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">No Accountability Chart documents uploaded yet.</p>
                )}
              </div>
              
              <FileUpload 
                onFileUpload={handleDocumentUpload} 
                disabled={isUploading}
                onExtractingStateChange={setIsUploading}
              />
            </div>
          </motion.div>
        );
      case 'core_process':
        return (
          <motion.div 
            key="core-process-view"
            className="h-full flex flex-col p-8"
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <div className="bg-white/50 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-orange-100/50 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <LordIcon 
                    icon={coreicon} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                  <h2 className="text-2xl font-semibold text-orange-700">Core Process</h2>
                </div>
              </div>
              
              <p className="text-orange-600 mb-4">
                Upload your Core Process documents to include them as context in your chats. These documents will help the AI understand your key business processes, workflows, and standard operating procedures.
              </p>
              
              <div className="bg-orange-50/50 rounded-lg p-4 mb-4 border border-orange-100">
                <h3 className="font-medium text-orange-700 mb-2">Current Documents</h3>
                {documentData.core_process && documentData.core_process.length > 0 ? (
                  <ul className="max-h-48 overflow-y-auto bg-white/70 rounded-lg divide-y divide-orange-100">
                    {documentData.core_process.map((doc, index) => (
                      <li key={index} className="flex items-center justify-between p-3">
                        <div className="flex items-center">
                          <LordIcon 
                            icon={fileIcon} 
                            size={20} 
                            trigger="hover"
                            colors="primary:#f97316"
                          />
                          <div>
                            <p className="text-sm font-medium">{doc.name}</p>
                            <p className="text-xs text-gray-500">Uploaded {new Date(doc.uploadDate).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleDeleteDocument(index)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1"
                        >
                          <LordIcon 
                            icon={trash} 
                            size={20} 
                            trigger="hover"
                            colors="primary:#ef4444"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">No Core Process documents uploaded yet.</p>
                )}
              </div>
              
              <FileUpload 
                onFileUpload={handleDocumentUpload} 
                disabled={isUploading}
                onExtractingStateChange={setIsUploading}
              />
            </div>
          </motion.div>
        );
      case 'chat':
      default:
        return (
          <motion.div 
            key="chat-view"
            className="h-full w-full"
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {chatId ? (
              <ChatWindow 
                chatId={chatId} 
                contextDocuments={documentData} 
                onDocumentsUpdated={handleDocumentsUpdated} 
              />
            ) : (
              <WelcomeChat />
            )}
          </motion.div>
        );
    }
  };

  // Render the appropriate view based on activeView state 
  const renderView = () => {
    return (
      <AnimatePresence mode="wait">
        {getViewComponent(activeView)}
      </AnimatePresence>
    );
  };

  return (
    <>
      {/* Floating sidebar for expanded mode */}
      <FloatingSidebar isExpanded={isExpanded} onToggleExpand={onToggleExpand} />
      
      <motion.div
        className="bg-white rounded-3xl shadow-sm overflow-hidden flex flex-col"
        style={{
          position: isExpanded ? 'fixed' : 'relative',
          inset: isExpanded ? '1rem' : 'auto',
          height: isExpanded ? 'auto' : '100%',
          zIndex: isExpanded ? 50 : 'auto',
        }}
        layout
        layoutId="chat-container"
        transition={{ 
          type: 'spring', 
          stiffness: 300, 
          damping: 30,
          duration: 0.5
        }}
        initial={false}
        animate={{
          scale: 1,
          opacity: 1
        }}
      >
        {/* Hover detection area */}
        <div 
          className="absolute top-0 left-0 w-24 h-24 z-40"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Expand/Collapse Button - only show when not expanded */}
          <AnimatePresence>
            {!expandAnimation && !isExpanded && (isHovering || isExpanded) && (
              <motion.button
                key="expand-button"
                onClick={handleExpandClick}
                className="absolute top-4 left-4 z-50 rounded-full p-2.5 bg-orange-50/70 backdrop-blur-sm hover:bg-orange-100/70 text-orange-700 shadow-sm flex items-center justify-center"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.2 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Fullscreen"
                style={{ width: '40px', height: '40px' }}
              >
                {/* Enter fullscreen icon */}
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
                  <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
                  <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
                  <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
                </svg>
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Render the appropriate view based on activeView state */}
        <div className="flex-1 overflow-auto">
          {renderView()}
        </div>
      </motion.div>
    </>
  );
};

export default ChatContainer; 